import React, { useEffect, useState } from 'react';
import { SearchIcon, MapPinIcon, UserIcon, ShoppingCartIcon, MenuIcon, ChevronDownIcon, HeartIcon } from 'lucide-react';
interface HeaderProps {
  onNavigate: (page: 'home' | 'shop' | 'product') => void;
  onOpenCart: () => void;
  onOpenMobileMenu: () => void;
}
export function Header({
  onNavigate,
  onOpenCart,
  onOpenMobileMenu
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <header className={`sticky top-0 z-40 bg-white transition-all duration-300 ${isScrolled ? 'shadow-soft' : 'border-b border-gray-100'}`} data-id="element-479">
      <div className="container-wide" data-id="element-480">
        {/* Top Row: Logo, Search, Actions */}
        <div className="flex items-center justify-between py-1 lg:py-1 gap-4 lg:gap-8" data-id="element-481">
          {/* Mobile Menu Toggle */}
          <button className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-brand-blue" onClick={onOpenMobileMenu} data-id="element-482">
            <MenuIcon className="w-6 h-6" data-id="element-483" />
          </button>

          {/* Logo - MASSIVELY INCREASED SIZE */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => onNavigate('home')} data-id="element-484">
            <img src="https://cdn.magicpatterns.com/uploads/sh7d1L5p7P4qM7QsZEZJDX/logo-header.png" alt="Better Pets and Gardens" className="h-28 md:h-32 lg:h-40 object-contain transition-all duration-300" data-id="element-485" />
          </div>

          {/* Search Bar (Desktop) */}
          <div className="hidden lg:flex flex-1 max-w-2xl relative" data-id="element-486">
            <input type="text" placeholder="Search pets, garden, pools..." className="w-full bg-warm-50 border border-gray-200 rounded-xl py-3.5 pl-12 pr-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:bg-white transition-all shadow-sm" data-id="element-487" />
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-488" />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-brand-blue text-white px-4 py-1.5 rounded-lg text-sm font-heading font-semibold hover:bg-brand-blue-600 transition-colors" data-id="element-489">
              Search
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6" data-id="element-490">
            {/* Store Selector (Desktop) */}
            <div className="hidden lg:flex items-center gap-3 cursor-pointer group" data-id="element-491">
              <div className="w-10 h-10 rounded-full bg-brand-blue-50 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors" data-id="element-492">
                <MapPinIcon className="w-5 h-5" data-id="element-493" />
              </div>
              <div className="flex flex-col" data-id="element-494">
                <span className="text-xs text-gray-500 font-medium" data-id="element-495">
                  Your Store
                </span>
                <div className="flex items-center gap-1 text-sm font-heading font-bold text-gray-900 group-hover:text-brand-blue transition-colors" data-id="element-496">
                  Select Store <ChevronDownIcon className="w-4 h-4" data-id="element-497" />
                </div>
              </div>
            </div>

            {/* Account (Desktop) */}
            <div className="hidden md:flex items-center gap-3 cursor-pointer group" data-id="element-498">
              <div className="w-10 h-10 rounded-full bg-warm-50 flex items-center justify-center text-gray-600 group-hover:bg-gray-100 transition-colors" data-id="element-499">
                <UserIcon className="w-5 h-5" data-id="element-500" />
              </div>
              <div className="flex flex-col hidden xl:flex" data-id="element-501">
                <span className="text-xs text-gray-500 font-medium" data-id="element-502">
                  Account
                </span>
                <span className="text-sm font-heading font-bold text-gray-900 group-hover:text-brand-blue transition-colors" data-id="element-503">
                  Sign In
                </span>
              </div>
            </div>

            {/* Mobile Search Icon */}
            <button className="lg:hidden p-2 text-gray-600 hover:text-brand-blue" data-id="element-504">
              <SearchIcon className="w-6 h-6" data-id="element-505" />
            </button>

            {/* Cart */}
            <button className="relative p-2 text-gray-600 hover:text-brand-blue group flex items-center gap-3" onClick={onOpenCart} data-id="element-506">
              <div className="relative" data-id="element-507">
                <ShoppingCartIcon className="w-7 h-7" data-id="element-508" />
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-brand-green text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm" data-id="element-509">
                  2
                </span>
              </div>
              <span className="hidden xl:block text-base font-heading font-bold text-gray-900 group-hover:text-brand-blue transition-colors" data-id="element-510">
                $159.89
              </span>
            </button>
          </div>
        </div>

        {/* Bottom Nav (Desktop) */}
        <nav className="hidden lg:flex items-center justify-between py-1 border-t border-gray-100" data-id="element-511">
          <ul className="flex items-center gap-8" data-id="element-512">
            <li data-id="element-513">
              <button onClick={() => onNavigate('shop')} className="font-heading font-semibold text-gray-800 hover:text-brand-blue transition-colors relative group py-2 text-[15px]" data-id="element-514">
                Shop Pets
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-blue transition-all duration-300 group-hover:w-full" data-id="element-515"></span>
              </button>
            </li>
            <li data-id="element-516">
              <button onClick={() => onNavigate('shop')} className="font-heading font-semibold text-gray-800 hover:text-brand-green transition-colors relative group py-2 text-[15px]" data-id="element-517">
                Shop Garden
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-green transition-all duration-300 group-hover:w-full" data-id="element-518"></span>
              </button>
            </li>
            <li data-id="element-519">
              <button onClick={() => onNavigate('shop')} className="font-heading font-semibold text-gray-800 hover:text-brand-blue transition-colors relative group py-2 text-[15px]" data-id="element-520">
                Shop Pools & Spas
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-blue transition-all duration-300 group-hover:w-full" data-id="element-521"></span>
              </button>
            </li>
            <li data-id="element-522">
              <button className="font-heading font-semibold text-red-600 hover:text-red-700 transition-colors relative group py-2 text-[15px]" data-id="element-523">
                Specials
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full" data-id="element-524"></span>
              </button>
            </li>
            <li data-id="element-525">
              <button className="font-heading font-semibold text-gray-800 hover:text-brand-blue transition-colors relative group py-2 text-[15px]" data-id="element-526">
                Stores
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-blue transition-all duration-300 group-hover:w-full" data-id="element-527"></span>
              </button>
            </li>
            <li data-id="element-528">
              <button className="font-heading font-semibold text-gray-800 hover:text-brand-blue transition-colors relative group py-2 text-[15px]" data-id="element-529">
                Advice
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-blue transition-all duration-300 group-hover:w-full" data-id="element-530"></span>
              </button>
            </li>
          </ul>

          <button className="flex items-center gap-2 text-brand-blue font-heading font-bold hover:text-brand-blue-700 transition-all hover:bg-brand-blue-100 bg-brand-blue-50 px-5 py-2.5 rounded-xl shadow-sm" data-id="element-531">
            <HeartIcon className="w-4 h-4 fill-current" data-id="element-532" />
            Customer Care Club
          </button>
        </nav>
      </div>
    </header>;
}